import { Box } from '@sequencehq/core-components'
import { CSSProperties, FC, ReactNode } from 'react'

import { useDropzone } from 'react-dropzone'
import { ServerAsset } from '@sequencehq/api'
import { validatePickedFiles } from 'Assets/utils'
import { useUploadAssets } from 'Assets/hooks/useUploadAssets'

type Props = {
  children: ReactNode
  maximumNumberOfFiles?: number
  onUploadFailure?: (error: string) => void
  onFilesPicked?: (files: File[]) => void
  onUploadSuccess: (asset: ServerAsset) => void | Promise<void>
  onAllUploadsComplete?: () => void
  containerStyle?: CSSProperties
  overlayDragActive: ReactNode
  accept: { extension: string; mimeType: string }
}

export const UploadAssetDropzone: FC<Props> = ({
  children,
  maximumNumberOfFiles = Infinity,
  onUploadFailure,
  onFilesPicked,
  onUploadSuccess,
  onAllUploadsComplete,
  containerStyle,
  overlayDragActive,
  accept
}) => {
  const uploadAssets = useUploadAssets()

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop: async files => {
      const hasDisallowedFileTypes = files.some(file => {
        return file.type !== accept.mimeType
      })

      if (hasDisallowedFileTypes) {
        onUploadFailure?.(`Only ${accept.extension} files can be uploaded`)
        return
      }

      const validationError = validatePickedFiles({
        files,
        maximumNumberOfFiles
      })

      if (validationError) {
        onUploadFailure?.(validationError)
        return
      }

      onFilesPicked?.(files)

      await uploadAssets({ files, onUploadSuccess, onUploadFailure })

      onAllUploadsComplete?.()
    },
    noClick: true,
    multiple: true
  })

  return (
    <Box {...getRootProps()} style={containerStyle} data-testid="dropzone">
      <input {...getInputProps()} />
      {children}
      {isDragActive && overlayDragActive}
    </Box>
  )
}
